import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ReactSVG } from "react-svg";

import NewToolTip from "../../../../common/New-ToolTip";
import { formatDateToDD_MMM_YYYY } from "../../../../../helpers/functions";
import { MedicationReviewDocTypes } from "../../../clinical-services/service-steps/documentation/data";
import {
  getEnabledInterventionForms,
  getInterventionCompletedDate,
  InterventionFormTypes,
  isAnnualMedicationReviewIntervention,
} from "../data";

/**
 * Row styling for a documentation form entry. Disabled forms are dimmed, the
 * selected form is highlighted and the rest stay in the neutral resting state.
 */
const getFormRowClassName = (isEnabled, isSelected) => {
  if (!isEnabled) return "bg-gray-20 border-gray-200 cursor-not-allowed opacity-60";
  return isSelected
    ? "bg-teal-25 border-teal-200"
    : "bg-gray-10 border-gray-100 hover:bg-blue-50";
};

/** Label styling that matches the row states above. */
const getFormLabelClassName = (isEnabled, isSelected) => {
  if (!isEnabled) return "text-gray-500";
  return isSelected ? "text-teal-800 font-semibold" : "text-gray-800";
};

/**
 * Final review card listing the documentation forms produced by an intervention.
 * Shared by the Documentation step and the Summary (final review) step.
 */
const DocumentationFormsCard = ({ intervention }) => {
  const navigate = useNavigate();
  const { interventionId } = useParams();

  const [selectedFormId, setSelectedFormId] = useState(null);
  const [hoveredFormId, setHoveredFormId] = useState(null);

  const { currentLoggedInUserDetail: userFromCurrentState } = useSelector(
    (state) => state.currentLoggedInUserDetail ?? {}
  );
  const usersState = useSelector((state) => state.users ?? {});
  const { currentLoggedInUserDetail: userFromUsers } = usersState;
  const currentLoggedInUserDetail = userFromCurrentState ?? userFromUsers;
  const pharmacistName =
    [currentLoggedInUserDetail?.firstName, currentLoggedInUserDetail?.lastName]
      .filter(Boolean)
      .join(" ") || "";

  const completedDate = getInterventionCompletedDate(intervention);

  const isAmrIntervention = isAnnualMedicationReviewIntervention(intervention);
  const visibleForms = getEnabledInterventionForms(intervention).filter((item) => {
    if (item.enable !== true) return false;
    // AMR-only forms should never be shown for non-AMR interventions.
    const isAmrOnlyForm =
      item.type === InterventionFormTypes.MedicationRecord ||
      item.type === InterventionFormTypes.AMRFollowUp ||
      item.type === InterventionFormTypes.ProviderNotification;
    return isAmrIntervention || !isAmrOnlyForm;
  });

  const handleFormClick = (form) => {
    if (!form.enable) return;
    const isNotesForm = form.type === InterventionFormTypes.Notes;
    const type = isNotesForm ? "AMR" : form?.section || "INV";
    const formType = isNotesForm ? MedicationReviewDocTypes.Notepad : form.type;
    const formUrl = `/patient/${intervention?.patientId}/interventions/${interventionId}/${type}/form/${formType}`;
    navigate(formUrl, { state: { fromDocumentationForms: true } });
  };

  return (
    <div className="w-[650px] min-h-[581px] rounded-[10px] border border-gray-100 bg-white flex flex-col">
      <div className="flex-col w-[650px] h-[92px] gap-[15px] opacity-1 pt-[12px] pr-[24px] pl-[24px] flex items-center justify-center">
        <h3 className="text-gray-800 font-semibold text-[22px]">
          Documentation Forms
        </h3>
        <div className={`w-full h-[1px] bg-gray-100`} />
      </div>
      <div className="flex-1 pb-6 px-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-1 justify-between text-sm text-gray-700">
            <div>
              <p className="font-regular">
                Created By:{" "}
                <span className="font-semibold">
                  {" "}
                  {pharmacistName || intervention?.createdBy || ""}
                </span>
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div>
                Date Created:{" "}
                <span className="font-semibold">
                  {intervention?.createdDate
                    ? formatDateToDD_MMM_YYYY(intervention.createdDate)
                    : ""}
                </span>
              </div>
              {completedDate && (
                <div>
                  Date Completed:{" "}
                  <span className="font-semibold">
                    {formatDateToDD_MMM_YYYY(completedDate)}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="text-base text-gray-800 ">
            Clinical Service Type:{" "}
            <span className="font-semibold text-teal-300">
              {intervention?.clinicalServiceType || ""}
            </span>
          </div>

          <div className={`w-full h-[1px] bg-gray-100 my-2`} />
          {visibleForms.map((item) => {
            const isEnabled = item.enable;
            const isSelected = selectedFormId === item.id;
            const isHovered = hoveredFormId === item.id;
            const formIconTooltipId = `documentation-form-icon-${item.id}`;
            const formRowClassName = getFormRowClassName(isEnabled, isSelected);
            const formLabelClassName = getFormLabelClassName(isEnabled, isSelected);

            return (
              <button
                key={item.id}
                type="button"
                className={`w-full border rounded flex items-center justify-between my-2 px-6 ${formRowClassName}`}
                onClick={() => {
                  if (isEnabled) {
                    setSelectedFormId(item.id);
                  }
                }}
                onMouseEnter={() => {
                  if (isEnabled) {
                    setHoveredFormId(item.id);
                  }
                }}
                onMouseLeave={() => setHoveredFormId(null)}
                disabled={!isEnabled}
              >
                <span className={`text-lg py-4 ${formLabelClassName}`}>
                  {item.name}
                </span>
                <button
                  type="button"
                  className={`p-1 rounded ${
                    isEnabled ? "cursor-pointer" : "cursor-not-allowed"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent button click
                    if (isEnabled) {
                      handleFormClick(item);
                    }
                  }}
                  disabled={!isEnabled}
                >
                  <ReactSVG
                    src={isSelected || isHovered ? item.activeIcon : item.inActiveIcon}
                    data-tooltip-id={formIconTooltipId}
                    data-tooltip-content={item.name}
                    className="w-6 h-6 flex flex-col justify-center"
                  />
                  <NewToolTip
                    id={formIconTooltipId}
                    place="bottom"
                    render={() => (
                      <div className="bg-gray-10 text-sm text-teal-300 px-3 py-1">
                        {item.name}
                      </div>
                    )}
                  />
                </button>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DocumentationFormsCard;
